<template>
  <div class="rt-root">
    <div
      class="rt-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="AI text">
      <div class="rt-header">
        <span
          class="rt-icon"
          aria-hidden="true">
          <icon name="sparkle"/>
        </span>
        <div class="rt-copy">
          <h2>Rewrite text</h2>
          <p>A fresh version of the selected text.</p>
        </div>
      </div>

      <div class="rt-body">
        <div class="rt-block">
          <span class="rt-kicker">Original</span>
          <p class="rt-original">{{ originalText }}</p>
        </div>

        <div
          v-if="isLoading"
          class="rt-block">
          <span class="rt-kicker">Rewritten</span>
          <div class="rt-skel-card">
            <span class="rt-skel is-long"/>
            <span class="rt-skel is-mid"/>
            <span class="rt-skel is-short"/>
          </div>
        </div>

        <div
          v-else-if="hasRewrite"
          class="rt-block">
          <span class="rt-kicker">Rewritten</span>
          <p class="rt-rewrite">{{ texts[0] }}</p>
        </div>

        <div
          v-else-if="hasError"
          class="rt-alert"
          role="alert">
          <icon name="alert-circle"/>
          <div class="rt-alert-copy">
            <span class="rt-alert-title">Couldn't rewrite this text</span>
            <span class="rt-alert-detail">The request failed. Your text hasn't changed.</span>
          </div>
        </div>
      </div>

      <div class="rt-footer">
        <span
          v-if="isLoading"
          class="rt-loading">
          <span
            class="rt-spin"
            aria-hidden="true"/>
          Loading...
        </span>
        <button
          v-else-if="hasRewrite"
          type="button"
          class="rt-btn is-ghost"
          @click="getRephrasedText">
          <icon name="refresh"/>
          Try again
        </button>
        <span class="rt-spacer"/>
        <button
          type="button"
          class="rt-btn is-outline"
          @click="$store.commit('activeProduct/updateShowTextAiRewriteModal', false)">
          Cancel
        </button>
        <button
          v-if="hasRewrite"
          type="button"
          class="rt-btn is-solid"
          @click="updateText">
          Use this text
        </button>
        <button
          v-else-if="hasError"
          type="button"
          class="rt-btn is-solid"
          @click="getRephrasedText">
          <icon name="refresh"/>
          Try again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EventBus from '../../EventBus';
import ApplicationEventTypes from '../../mixins/ApplicationEventTypes';
import Icon from '../Icon/Icon.vue';

export default {
  components: { Icon },
  data() {
    return {
      showResults: false,
      isLoading:   true,
      hasError:    false,
      texts:       [],
      requestId:   0,
    };
  },
  computed: {
    originalText() {
      return this.$store.getters['activeProduct/getCurrentObjectText'] || '';
    },
    hasRewrite() {
      return !this.isLoading && !this.hasError && this.texts.length > 0;
    },
  },
  mounted() {
    this.getRephrasedText();
  },
  methods: {
    failRewrite(requestId) {
      if (requestId !== this.requestId) return;
      this.texts       = [];
      this.isLoading   = false;
      this.hasError    = true;
      this.showResults = false;
    },
    getRephrasedText() {
      const self               = this;
      this.requestId          += 1;
      const { requestId }      = this;
      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const userId             = newUrlSearchParams.has('userId')
        ? newUrlSearchParams.get('userId')
        : '21d29854-fb88-45e6-9a12-b1f4e6c67d45';

      const domainUrl = this.$store.getters.getBMCInstance === 'marketingcenter.source4'
        ? this.$store.getters.getBMCInstance
        : `www.${this.$store.getters.getBMCInstance}`;

      const numberofVariations = 1;

      // clear previous results
      self.texts       = [];
      this.showResults = false;
      this.isLoading   = true;
      this.hasError    = false;
      let prompt       = this.originalText;
      prompt           = prompt.replaceAll('&', 'and');

      for (let i = 0; i < numberofVariations; i++) {
        const postingUrl = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=GetAiResponse&bankId=${this.$store.getters.getBankId}&domainURL=https://${domainUrl}.com/&userId=${userId}&userInput=${encodeURIComponent(`Write alternative text to what's written below. The response should only consist of the new text. The new text should be in plain text and not contain em dashes.

${prompt} `)}&numberOfVariations=1`;

        axios.post(postingUrl)
          .then((response) => {
            if (requestId !== self.requestId) return;
            if (response.data) {
              const parser = new DOMParser();
              const xmlDoc = parser.parseFromString(response.data, 'text/xml');
              const resultNode = xmlDoc.getElementsByTagName('GetAiResponseResult')[0];

              if (!resultNode) {
                self.failRewrite(requestId);
                return;
              }

              const result = resultNode.textContent.trimStart();

              self.texts.push(result);
              this.isLoading = false;

              // Show results as soon as the first request finishes
              if (!this.showResults) {
                this.showResults = true;
              }
            } else {
              self.failRewrite(requestId);
            }
          })
          .catch((e) => {
            console.error('Error fetching variation:', e);
            self.failRewrite(requestId);
          });
      }
    },
    updateText() {
      EventBus.$emit(ApplicationEventTypes.Canvas.UPDATE_TEXT_FROM_AI, this.texts[0].split(/\n\s*\n/) // split on blank lines
        .map(p => `<p>${p.trim()}</p><br>`)
        .join(''));
      this.$store.commit('activeProduct/updateShowTextAiRewriteModal', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.rt-root {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(2px);
  font-family: 'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: #14161B;
  -webkit-font-smoothing: antialiased;
}

.rt-dialog {
  position: relative;
  width: 800px;
  max-width: 100%;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 0 0 1px rgba(16, 24, 40, 0.06), 0 24px 64px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}

.rt-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 0;
}

.rt-icon {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #EAF2FE;
  color: #0B57C2;
}

.rt-copy h2 {
  margin: 0;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: #14161B;
}

.rt-copy p {
  margin: 2px 0 0 !important;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.4;
  color: #5E6573;
}

.rt-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 18px 24px 20px;
}

.rt-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rt-kicker {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: #5E6573;
}

.rt-original,
.rt-rewrite {
  margin: 0 !important;
  white-space: pre-wrap;
}

.rt-original {
  padding: 12px 14px;
  border-radius: 10px;
  background: #F3F4F6;
  font-size: 14px;
  line-height: 1.55;
  color: #3D4350;
}

.rt-rewrite {
  padding: 14px 16px;
  border-radius: 12px;
  background: #F7FAFF;
  box-shadow: inset 0 0 0 1.5px #1570EF;
  font-size: 15px;
  line-height: 1.55;
  color: #14161B;
}

.rt-skel-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px #E7E9ED;
}

.rt-skel {
  display: block;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #E7E9ED 0%, #F6F7F9 40%, #E7E9ED 80%);
  background-size: 200% 100%;
  animation: rt-shimmer 1.4s linear infinite;
}

.rt-skel.is-long {
  width: 92%;
}

.rt-skel.is-mid {
  width: 76%;
}

.rt-skel.is-short {
  width: 54%;
}

.rt-alert {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #FEF3F2;
  box-shadow: inset 0 0 0 1px #FBC9C4;
}

.rt-alert-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rt-alert-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: #912018;
}

.rt-alert-detail {
  font-size: 12.5px;
  line-height: 1.4;
  color: #912018;
}

.rt-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid #E7E9ED;
  background: #FBFBFC;
}

.rt-spacer {
  flex: 1 1 auto;
}

.rt-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 1;
  color: #5E6573;
}

.rt-spin {
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid #E7E9ED;
  border-top-color: #1570EF;
  animation: rt-spin 0.8s linear infinite;
}

.rt-btn {
  height: 38px;
  min-height: 38px;
  min-width: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 14px;
  border: 0;
  border-radius: 9px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}

.rt-btn,
.rt-btn:focus {
  border: 0;
  outline: 0;
  -webkit-appearance: none;
  appearance: none;
}

.rt-btn.is-ghost {
  background: transparent;
  color: #14161B;
  box-shadow: none;
}

.rt-btn.is-ghost:hover {
  background: #F3F4F6;
}

.rt-btn.is-outline {
  background: #fff;
  color: #14161B;
  box-shadow: inset 0 0 0 1px #D5D9E0;
}

.rt-btn.is-outline:hover {
  background: #F3F4F6;
}

.rt-btn.is-solid {
  background: #1570EF;
  color: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
}

.rt-btn.is-solid:hover {
  background: #0B57C2;
}

.rt-icon ::v-deep .icon,
.rt-btn ::v-deep .icon,
.rt-alert ::v-deep .icon {
  min-width: 0;
  min-height: 0;
  flex-shrink: 0;
}

.rt-icon ::v-deep svg {
  display: block;
  width: 18px;
  height: 18px;
}

.rt-btn ::v-deep svg,
.rt-alert ::v-deep svg {
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.rt-alert ::v-deep .icon {
  color: #D92D20;
  padding-top: 1px;
}

@keyframes rt-spin {
  to { transform: rotate(360deg); }
}

@keyframes rt-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
</style>
